/**
 * A type-to-search picker for a Frappe **Link** field.
 *
 * ---- It behaves like a filled-in field, not a summary ----
 *
 * Once a value is chosen the input KEEPS SHOWING IT and stays editable. An earlier version
 * replaced the field with a chip plus a "change" link, which read as a read-only summary of a
 * completed step: to correct a wrong customer you first had to notice the chip was interactive,
 * click "change", and start from an empty box. Now the value is simply there, and typing over it
 * searches for a different one — the same as any other form field on the screen.
 *
 * Nothing is lost by clicking away: an abandoned search reverts to the value that was already set,
 * so a stray focus cannot silently blank a field the purchaser had filled.
 *
 * Deliberately not a `<select>`: the live bench's `Customer` list runs to thousands of rows, so
 * options are fetched per search term rather than pulled up front. The value shown is the docname
 * (`3 STAR MARINE SERVICES`) — the exact string written to the document, so a wrong pick is
 * visible rather than hidden behind a friendlier label.
 *
 * `useOptions` is a hook supplied by the caller (`useCustomerSearch` closed over a company, `useCompanyPicker`, …). It is
 * called exactly once per render, so hook order is stable.
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { PickerOption } from '../lib/api/useEnquiryFromMail'

/**
 * The control itself — input plus dropdown, with no label and no layout wrapper.
 *
 * Split out because two screens need identical behaviour inside different markup: the enquiry
 * panel's `.fieldrow` grid and the RFQ header card's `.ef` stacked label. Sharing the widget
 * rather than the wrapper keeps the search, revert-on-abandon and keyboard handling in one place.
 */
export function LinkCombo({
  id,
  value,
  onChange,
  useOptions,
  placeholder,
  disabled,
}: {
  id?: string
  value: string
  onChange: (next: string) => void
  useOptions: (search: string) => { data?: PickerOption[]; isPending: boolean }
  placeholder: string
  disabled?: boolean
}) {
  const [term, setTerm] = useState<string | null>(null)
  const containerRef = useRef<HTMLSpanElement | null>(null)

  const editing = term !== null
  const { data, isPending } = useOptions(editing ? term : '')

  useEffect(() => {
    if (!editing) return
    const onDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target)) return
      /*
        The list lives in `document.body` now (see the portal below), so `containerRef` no longer
        contains it — and without this check a pointerdown on an option counts as "outside",
        closes the list, unmounts the button, and the click never lands. The field would look
        completely inert.
      */
      if (target instanceof Element && target.closest('[data-linkcombo-list]')) return
      setTerm(null)
    }
    document.addEventListener('pointerdown', onDocumentPointerDown)
    return () => document.removeEventListener('pointerdown', onDocumentPointerDown)
  }, [editing])

  const commit = (next: string) => {
    onChange(next)
    setTerm(null)
  }

  /*
    ---- The list is rendered into `document.body`, not beside the input ----

    It used to be `position: absolute` inside the field's own wrapper, which works everywhere the
    field has room — and fails completely inside a scroll container. In the quotation's taxes grid
    (`overflow-x: auto` on the table, so wide tables scroll rather than break the page) the list was
    CLIPPED to the row and had to be scrolled to inside it, which is unusable.

    A portal takes it out of every ancestor's overflow and stacking context at once. The trade is
    that it no longer moves with the field automatically, so the position is measured when it opens
    and refreshed on scroll and resize — the two things that move a field under a fixed element.
  */
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null)
  useEffect(() => {
    if (!editing) {
      setAnchor(null)
      return
    }
    const place = () => {
      const box = containerRef.current?.getBoundingClientRect()
      if (!box) return
      // Flipped above the field when there is not room below — near the foot of a long quotation
      // the list would otherwise open off-screen.
      const below = window.innerHeight - box.bottom
      const top = below < 200 && box.top > below ? box.top - Math.min(190, box.top) - 4 : box.bottom + 4
      setAnchor({ top, left: box.left, width: box.width })
    }
    place()
    // `true` — capture phase, so a scroll on ANY ancestor is caught, not just the window.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [editing])

  return (
    <span ref={containerRef} style={{ position: 'relative', display: 'block' }}>
      <input
        id={id}
        type="text"
        value={editing ? term : value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => setTerm(e.target.value)}
        onFocus={(e) => {
          setTerm(value)
          e.target.select()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setTerm(null)
          if (e.key === 'Enter' && (data ?? []).length > 0) {
            e.preventDefault()
            commit((data ?? [])[0].name)
          }
        }}
        style={{ width: '100%' }}
      />
      {editing && anchor
        ? createPortal(
        <div
          // `pointerdown` closes on a click outside; the listener checks `containerRef`, so this
          // marks the list as part of the field even though it is no longer inside it in the DOM.
          data-linkcombo-list=""
          style={{
            position: 'fixed',
            top: anchor.top,
            left: anchor.left,
            width: anchor.width,
            // Above modals, which is where these fields most often live.
            zIndex: 1000,
            maxHeight: 190,
            overflow: 'auto',
            border: '1px solid var(--line)',
            borderRadius: 8,
            marginTop: 4,
            background: 'var(--panel)',
            boxShadow: '0 10px 30px rgba(0,0,0,.18)',
          }}
        >
          {isPending ? (
            <div className="pad" style={{ color: 'var(--muted)' }}>
              Searching…
            </div>
          ) : null}
          {!isPending && (data ?? []).length === 0 ? (
            <div className="pad" style={{ color: 'var(--muted)' }}>
              No match.
            </div>
          ) : null}
          {(data ?? []).map((option) => (
            <button
              key={option.name}
              type="button"
              className="srow"
              onClick={() => commit(option.name)}
              // The value already in force is greyed the way the ERP desk greys it, so the list
              // says "you are on this one" without a second control to read.
              style={option.name === value ? { background: 'var(--bg)' } : undefined}
            >
              <span className="nm">
                {option.label}
                {option.note ? (
                  <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {option.note}</span>
                ) : null}
              </span>
              {/* The second line exists so a list can answer "and what does that MEAN?" without
                  being opened once per option — see `PickerOption.sublabel`. */}
              {option.sublabel ? (
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                  {option.sublabel}
                </span>
              ) : null}
            </button>
          ))}
        </div>,
            document.body,
          )
        : null}
    </span>
  )
}

export function LinkPicker({
  label,
  value,
  onChange,
  useOptions,
  placeholder,
  required = true,
  disabled = false,
  hint,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  useOptions: (search: string) => { data?: PickerOption[]; isPending: boolean }
  placeholder: string
  required?: boolean
  /** Same idiom `EnquiryHeaderCard.tsx`'s `TextField` uses for a field its write endpoint dropped:
   * grey the input out rather than silently ignoring what's typed into it, and say why below. */
  disabled?: boolean
  hint?: string
}) {
  return (
    <div className="fieldrow">
      <label htmlFor={`lp-${label}`}>
        {label}
        {required && !disabled && value === '' ? <span style={{ color: 'var(--danger)' }}> *</span> : null}
      </label>
      <div>
        <LinkCombo id={`lp-${label}`} value={value} onChange={onChange} useOptions={useOptions} placeholder={placeholder} disabled={disabled} />
        {hint ? <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>{hint}</div> : null}
      </div>
    </div>
  )
}
